/* ============================================================================
   Sticker studio (Codex new-experience page) — Enhance.
   Upscales low-resolution images toward 300 DPI at their printed size.
   • AI path: Pruna's upscale model through Cloudflare AI via dev-studio-cloud-upscale.js.
     The older browser engine remains available only if the cloud adapter is absent.
   • When the cloud adapter is absent, a classic path handles missing WebGPU or local AI failures:
     stepped high-quality resample + unsharp mask + crisp alpha edges.
   Cloud requests upload PNG bytes to the isolated Dev enhancement Worker. The section exports window._sxEnhApi
   (getMainImage, replaceCanvasImage, getShapeDims, saveState, drawCutline,
   applyShape, showProgress, hideProgress) and stores _smsEnh = { by: 'ai'|'classic', from: DPI,
   w0: pixel width before enhancing } on enhanced images (kept in undo states via toJSON).
   Public: window._sxEnhance = { run, dpiOf, mainDpi, lowImages, cartNote }.
   ========================================================================== */
(function () {
  'use strict';
  var TARGET = 300, OK_DPI = 300, MAX_F = 8;

  function S() { return window._sms; }
  function A() { return window._sxEnhApi || {}; }
  function isIOS() { return /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); }
  function mk(w, h) { var c = document.createElement('canvas'); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h)); return c; }
  function ctx(c) { return c.getContext('2d', { willReadFrequently: true }); }
  function nat(e) { return [e.naturalWidth || e.width || 0, e.naturalHeight || e.height || 0]; }

  function images() {
    var s = S(), c = s && s.canvas; if (!c) return [];
    return c.getObjects().filter(function (o) { return o.type === 'image' && !o._isCutline && !o._isCutEdge && !o._isTemplateBg && o.visible !== false && o.getElement && o.getElement(); });
  }
  // printed inches per canvas pixel: die-cut uses the cut line's bounds, shapes use the shape size
  function inchPerPx() {
    var s = S(); if (!s || !(Number(s.sizeInches) > 0)) return 0;
    if(s.shape==='die-cut'&&Number(s.artworkUnitInches)>0)return Number(s.artworkUnitInches);
    if (s.shape === 'die-cut' && s.cutContourPaths && window.StudioCutlineCore) {
      var b = window.StudioCutlineCore.bounds(s.cutContourPaths);
      if (b && b.width > 0 && b.height > 0) return Number(s.sizeInches) / Math.max(b.width, b.height);
    }
    var a = A();
    if (s.shape !== 'die-cut' && a.getShapeDims) { var d = a.getShapeDims(); if (d && d[0] > 0) return Number(s.sizeInches) / Math.max(d[0], d[1]); }
    return 0;
  }
  // source pixels per printed inch for one image object (same method as the note under the canvas)
  function dpiOf(o) {
    var e = o && o.getElement && o.getElement(); if (!e) return 0;
    var u = inchPerPx(), m = o.calcTransformMatrix(), st = Math.max(Math.hypot(m[0], m[1]), Math.hypot(m[2], m[3]));
    if (u > 0 && st > 0) return 1 / (st * u);
    var n = nat(e), s = S(); return s && s.sizeInches > 0 ? Math.max(n[0], n[1]) / s.sizeInches : 0;
  }
  function mainDpi() {
    var g = A().getMainImage, m = g && g();
    if (m && m._isTemplateBg) { var d = images().map(dpiOf).filter(function (v) { return v > 0; }); return d.length ? Math.min.apply(null, d) : 1000; } // template: its blank background isn't art
    return m ? dpiOf(m) : 0;
  }
  // enhancement info, or null once the pixels were swapped back (e.g. background set to None restores the un-enhanced upload)
  function enhInfo(o) {
    var e = o && o._smsEnh; if (!e) return null;
    if (typeof e === 'number') return { by: e < 0 ? 'ai' : 'classic', from: Math.abs(e) };
    if (!e.by) return null; // { ai: prompt } only: an AI edit, not an enhancement
    var el = o.getElement && o.getElement(); if (e.w0 && el && nat(el)[0] <= e.w0) return null;
    return e;
  }
  function lowImages() { return images().filter(function (o) { var d = dpiOf(o); return d > 0 && d < OK_DPI; }); }

  // ── classic path (ported from DTF Studio tools) ────────────────────────────
  function unsharp(d, w, h, radius, amount) {
    var n = w * h, tmp = new Float32Array(n * 3), blr = new Float32Array(n * 3), r = Math.max(1, Math.round(radius)), k = 1 / (2 * r + 1), c, x, y, s0, s1, s2, i, j;
    for (i = 0, j = 0; i < n; i++, j += 4) { blr[i * 3] = d[j]; blr[i * 3 + 1] = d[j + 1]; blr[i * 3 + 2] = d[j + 2]; }
    for (var pass = 0; pass < 2; pass++) {
      for (y = 0; y < h; y++) { s0 = s1 = s2 = 0; var row = y * w;
        for (x = -r; x <= r; x++) { c = (row + Math.min(w - 1, Math.max(0, x))) * 3; s0 += blr[c]; s1 += blr[c + 1]; s2 += blr[c + 2]; }
        for (x = 0; x < w; x++) { c = (row + x) * 3; tmp[c] = s0 * k; tmp[c + 1] = s1 * k; tmp[c + 2] = s2 * k;
          var add = (row + Math.min(w - 1, x + r + 1)) * 3, rem = (row + Math.max(0, x - r)) * 3; s0 += blr[add] - blr[rem]; s1 += blr[add + 1] - blr[rem + 1]; s2 += blr[add + 2] - blr[rem + 2]; } }
      for (x = 0; x < w; x++) { s0 = s1 = s2 = 0;
        for (y = -r; y <= r; y++) { c = (Math.min(h - 1, Math.max(0, y)) * w + x) * 3; s0 += tmp[c]; s1 += tmp[c + 1]; s2 += tmp[c + 2]; }
        for (y = 0; y < h; y++) { c = (y * w + x) * 3; blr[c] = s0 * k; blr[c + 1] = s1 * k; blr[c + 2] = s2 * k;
          var ad2 = (Math.min(h - 1, y + r + 1) * w + x) * 3, rm2 = (Math.max(0, y - r) * w + x) * 3; s0 += tmp[ad2] - tmp[rm2]; s1 += tmp[ad2 + 1] - tmp[rm2 + 1]; s2 += tmp[ad2 + 2] - tmp[rm2 + 2]; } }
    }
    for (i = 0, j = 0; i < n; i++, j += 4) {
      if (d[j + 3] < 250) continue; // opaque pixels only: sharpening against transparency makes halos
      for (var ch = 0; ch < 3; ch++) { var diff = d[j + ch] - blr[i * 3 + ch]; if (Math.abs(diff) > 4) d[j + ch] = d[j + ch] + amount * diff; }
    }
  }
  function crispAlpha(d) { for (var a = 3; a < d.length; a += 4) { var v = d[a] / 255; if (v > 0 && v < 1) { v = Math.min(1, Math.max(0, (v - 0.35) / 0.3)); d[a] = Math.round(v * v * (3 - 2 * v) * 255); } } }
  function classic(src, W, H, f, crisp) {
    var cur = src, cw = src.width, chh = src.height;
    while (cw * 2 < W) { var st = mk(cw * 2, chh * 2), sx = st.getContext('2d'); sx.imageSmoothingEnabled = true; sx.imageSmoothingQuality = 'high'; sx.drawImage(cur, 0, 0, st.width, st.height); if (cur !== src) cur.width = 1; cur = st; cw = st.width; chh = st.height; }
    var up = mk(W, H), ux = ctx(up); ux.imageSmoothingEnabled = true; ux.imageSmoothingQuality = 'high'; ux.drawImage(cur, 0, 0, W, H);
    if (cur !== src) cur.width = 1;
    var out = mk(W, H), ox = out.getContext('2d'), SL = 512, P = Math.ceil(f * 3) + 2;
    for (var y = 0; y < H; y += SL) {
      var y0 = Math.max(0, y - P), y1 = Math.min(H, y + SL + P), id = ux.getImageData(0, y0, W, y1 - y0), d = id.data;
      unsharp(d, W, y1 - y0, Math.min(2.5, f * 0.8), 0.6);
      if (crisp) crispAlpha(d);
      ox.putImageData(id, 0, y0, 0, y - y0, W, Math.min(SL, H - y));
    }
    up.width = up.height = 1;
    return out;
  }
  // which AI model: transparent art → 'art'; opaque → photo vs jpeg-ish graphics (from DTF aiKind)
  function aiKind(hasAlpha, src) {
    if (hasAlpha) return 'art';
    var n = 96, c = mk(n, n), x = ctx(c); x.drawImage(src, 0, 0, n, n);
    var d = x.getImageData(0, 0, n, n).data, seen = {}, uniq = 0, flat = 0;
    for (var i = 0; i < n * n; i++) {
      var q = (d[i * 4] >> 3) + '.' + (d[i * 4 + 1] >> 3) + '.' + (d[i * 4 + 2] >> 3); if (!seen[q]) { seen[q] = 1; uniq++; }
      if (i % n && Math.abs(d[i * 4] - d[i * 4 - 4]) + Math.abs(d[i * 4 + 1] - d[i * 4 - 3]) + Math.abs(d[i * 4 + 2] - d[i * 4 - 2]) < 6) flat++;
    }
    c.width = 1;
    return uniq > 1800 && flat / (n * n) < 0.45 ? 'photo' : 'jpeg';
  }
  function alphaStats(src) {
    var d = ctx(src).getImageData(0, 0, src.width, src.height).data, any = false, semi = 0, n = d.length / 4;
    for (var i = 3; i < d.length; i += 4) { var v = d[i]; if (v < 255) { any = true; if (v > 0) semi++; } }
    return { alpha: any, semi: semi / n };
  }
  function toImage(canvas, alpha) {
    return new Promise(function (res, rej) {
      canvas.toBlob(function (b) {
        if (!b) return rej(new Error('encode failed'));
        var fr = new FileReader();
        fr.onload = function () { var im = new Image(); im.onload = function () { res(im); }; im.onerror = function () { rej(new Error('decode failed')); }; im.src = fr.result; };
        fr.onerror = function () { rej(new Error('read failed')); }; fr.readAsDataURL(b);
      }, alpha ? 'image/png' : 'image/jpeg', 0.95);
    });
  }

  // swap one image object's pixels, keeping its on-canvas size/rotation/flip/order
  function swap(o, im) {
    var a = A(), s = S(), c = s.canvas, main = a.getMainImage && a.getMainImage();
    if (o === main && a.replaceCanvasImage) {
      // The result becomes the new base picture with background 'None' (any removal is already in its pixels).
      // Keeping it only as processedImg let the next background switch restore the pre-edit upload and lose the edit.
      s.originalImg = im; s.processedImg = null; s.bgMode = 'none';
      document.querySelectorAll('.sms-tool-icon[data-bg]').forEach(function (b) { b.classList.toggle('active', b.dataset.bg === 'none'); });
      a.replaceCanvasImage(im); // redraws the cut line and saves one undo step
      return;
    }
    var oldW = o.width, oldH = o.height, nw = im.naturalWidth, nh = im.naturalHeight;
    o.setElement(im, { width: nw, height: nh });
    o.set({ scaleX: o.scaleX * oldW / nw, scaleY: o.scaleY * oldH / nh, dirty: true }); o.setCoords();
    if (s.shape === 'die-cut') { if (a.drawCutline) a.drawCutline(); } else if (a.applyShape) a.applyShape();
    c.renderAll(); if (a.saveState) a.saveState();
  }

  var busy = false;
  var srcOf = new WeakMap(); // enhanced element -> the un-enhanced element it was made from (enhancing again starts from the original)
  async function run(list) {
    var s = S(), a = A();
    if (busy) return;
    if (!s || !s.canvas) return toast('Add an image first.', 'warn');
    if (s.bgPending) return toast('Wait for background removal to finish, then enhance.', 'info');
    if (!list || !list.length) {
      var act = s.canvas.getActiveObject(), sel = act ? (act.type === 'activeSelection' ? act.getObjects() : [act]) : [];
      sel = sel.filter(function (o) { return o.type === 'image' && !o._isCutline; });
      list = sel.length ? sel : lowImages();
    }
    if (!list.length) return toast(images().length ? 'Your images are already sharp enough to print (300 DPI).' : 'Add an image first.', 'ok');
    busy = true; refreshUi();
    var done = 0, aiDone = 0, refused = 0, capped = 0, stale = 0, failed = 0, failure = '', reached = [], LIM = isIOS() ? 12e6 : 25e6;
    try {
      for (var i = 0; i < list.length; i++) {
        var o = list[i], e = o.getElement(), eStart = e, n = nat(e), dpi = dpiOf(o), ei = enhInfo(o), w0 = 0;
        if (!(dpi > 0) || dpi >= OK_DPI) continue;
        var e0 = srcOf.get(e), n0 = e0 && nat(e0);
        if (n0 && n0[0] > 0) { dpi = dpi * n0[0] / n[0]; e = e0; n = n0; } // work from the original upload, and report its real DPI
        else if (ei && ei.w0 && ei.w0 < n[0]) w0 = ei.w0; // undo/redo rebuilt the element: the original is gone but its width is recorded
        var f = TARGET / dpi, dpiSrc = w0 ? dpi * w0 / n[0] : dpi;
        if (TARGET / dpiSrc > MAX_F) { refused++; continue; } // the 8x cap counts from the upload, not from an earlier enhance
        var W = Math.ceil(n[0] * f), H = Math.ceil(n[1] * f);
        if (W * H > LIM) { f = Math.sqrt(LIM / (n[0] * n[1])); W = Math.round(n[0] * f); H = Math.round(n[1] * f); if (f <= 1) { capped++; continue; } }
        if (f <= 1) continue;
        if (a.showProgress) a.showProgress('Enhancing image…');
        var src = mk(n[0], n[1]); ctx(src).drawImage(e, 0, 0, n[0], n[1]);
        var st = alphaStats(src), out = null, by = 'classic';
        try {
          if (window._sxCloudEnhance) {
            out = await window._sxCloudEnhance.upscale(src, {W:W,H:H,alpha:st.alpha,onProgress:function(p,t){if(a.showProgress)a.showProgress(t);}});
            by = 'ai';
          } else if (window.DTFUpscale && n[0] * n[1] * 16 <= 64e6 && await window.DTFUpscale.available()) {
            try {
              out = await window.DTFUpscale.upscale(src, { kind: aiKind(st.alpha, src), W: W, H: H, soft: st.semi > 0.05, onProgress: function (p, t) { if (a.showProgress) a.showProgress(t); } });
              by = 'ai';
            } catch (ae) { console.warn('[enhance] AI upscale failed — classic path', ae); out = null; }
          }
          if (!out) { if (a.showProgress) a.showProgress('Enhancing image…'); out = classic(src, W, H, f, st.alpha && !(st.semi > 0.05)); }
          var im = await toImage(out, st.alpha);
          if (o.getElement() !== eStart || S().canvas.getObjects().indexOf(o) < 0) { stale++; continue; } // changed while enhancing: keep the newer edit
          o._smsEnh = { by: by, provider: window._sxCloudEnhance && by === 'ai' ? 'cloudflare-pruna-v1' : by, from: Math.round(dpiSrc), w0: w0 || n[0], ai: o._smsEnh && o._smsEnh.ai };
          srcOf.set(im, e);
          swap(o, im);
          done++; if (by === 'ai') aiDone++; reached.push(Math.min(TARGET, Math.round(dpi * f)));
        } catch (err) { console.warn('[enhance]', err); failed++; failure=err.message || 'Enhancement failed. Please try again.'; }
        finally { src.width = src.height = 1; if (out) out.width = out.height = 1; }
      }
    } finally { busy = false; if (a.hideProgress) a.hideProgress(); if(window._sxRefreshDisplay)window._sxRefreshDisplay(); refreshUi(); }
    var t = document.getElementById('smsDpiToast'); if (t && done) t.remove();
    var low = reached.length ? Math.min.apply(null, reached) : TARGET;
    if (done) toast((aiDone ? 'AI-upscaled' : 'Enhanced') + (done > 1 ? ' ' + done + ' images' : '') + ' to ' + (low >= 295 ? '300' : '≈' + low) + ' DPI. ' + (aiDone ? 'Zoom in to check the result, especially small text.' : 'Smoothed and sharpened — it can’t add detail that isn’t in the file.') + (failed ? ' ' + failed + ' image' + (failed > 1 ? 's were' : ' was') + ' left unchanged because enhancement failed.' : ''), failed ? 'warn' : 'ok');
    else if (failed) toast(failure, 'warn');
    else if (refused) toast('This image is too small to enhance that far (more than 8×). Print it smaller or upload a bigger file.', 'warn');
    else if (stale) toast('Enhance was cancelled because the image changed. Tap Enhance again if you still need it.', 'warn');
    else if (capped) toast('This image is too large to enhance on this device. Try on a computer, or print it a little smaller.', 'warn');
    else toast('Nothing needed enhancing.', 'ok');
  }

  // cart note for production, e.g. "AI-upscaled from 48 DPI"
  function cartNote() {
    // 'from' = the original upload's DPI at the current print size (the size may have changed since enhancing)
    var ai = window._sxAiEdit && window._sxAiEdit.note(); // 'AI-edited: "..."' from dev-studio-ai-edit.js
    var enh = images().map(function (o) { var e = enhInfo(o); if (!e) return null; var el = o.getElement(), w = el && nat(el)[0], from = e.w0 && w ? Math.round(dpiOf(o) * e.w0 / w) : e.from; return (e.by === 'ai' ? 'AI-upscaled' : 'Enhanced') + ' from ' + from + ' DPI'; }).filter(Boolean).join('; ');
    return [ai, enh].filter(Boolean).join('; ');
  }

  // ── UI ─────────────────────────────────────────────────────────────────────
  (function css() {
    var st = document.createElement('style');
    st.textContent =
      '.sx-enh-row{display:flex;align-items:center;gap:8px}' +
      '.sx-enh-float{position:absolute;top:12px;left:12px;z-index:35;padding:6px;border:1px solid #d6e3f1;border-radius:12px;background:rgba(255,255,255,.96);box-shadow:0 3px 12px #24497118;max-width:calc(100% - 24px)}' +
      '.sx-enh-float[hidden],#sxEnhBtn[hidden],#sxEnhDpi[hidden]{display:none!important}.sx-enh-float .sx-enh-dpi{padding-right:6px;white-space:nowrap}' +
      '#sxEnhBtn{flex:0 0 auto;display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 12px;border-radius:8px;border:1px solid #d1d5db;background:#fff;color:#111827;font-family:inherit;font-size:12px;font-weight:600;line-height:1;cursor:pointer}' +
      '#sxEnhBtn:hover:not(:disabled){border-color:#16755b;color:#16755b}' +
      '#sxEnhBtn:disabled{opacity:.45;cursor:default}' +
      '#sxEnhBtn.sx-go,#sxEnhBtn.sx-go:hover{background:#16755b;border-color:#16755b;color:#fff}' +
      '.sx-enh-dpi{font-size:11px;color:#6b7280;line-height:1.3}' +
      '.sx-enh-dpi b{color:#b45309}' +
      'button[data-sx-enh][data-sx-enh]{flex:0 0 auto;height:28px;padding:0 10px;border-radius:7px;border:1px solid currentColor;background:#fff;color:inherit;font-family:inherit;font-size:12px;font-weight:700;line-height:1;cursor:pointer}' +
      '#sxEnhToast{position:fixed;left:0;right:0;bottom:24px;z-index:10000;display:flex;justify-content:center;pointer-events:none;transition:opacity .3s}' +
      '#sxEnhToast>div{pointer-events:auto;max-width:min(460px,calc(100vw - 24px));padding:10px 14px;border-radius:10px;font-size:13px;line-height:1.4;box-shadow:0 6px 20px rgba(0,0,0,.15);background:#111827;color:#fff}' +
      '#sxEnhToast.warn>div{background:#fff3cd;color:#7a5a04;border:1px solid #ffe08a}';
    (document.head || document.documentElement).appendChild(st); // note: the page resets transform/padding/max-width on direct children of <body>
  })();
  function toast(msg, kind) {
    var t = document.getElementById('sxEnhToast'); if (t) t.remove();
    t = document.createElement('div'); t.id = 'sxEnhToast'; t.className = kind === 'warn' ? 'warn' : ''; t.setAttribute('role', 'status'); t.appendChild(document.createElement('div')).textContent = msg;
    // same spot as the low-resolution warning: top of the canvas area, clear of the toolbars
    var fb = window._sxFreeBox && window._sxFreeBox(); // canvas area minus the Layers panel
    if (!fb) { var ar = document.querySelector('.sms-canvas-area'), rc = ar && ar.getBoundingClientRect(); if (rc && rc.width > 0 && rc.top < innerHeight) fb = { left: rc.left, right: rc.right, top: rc.top + 10 }; }
    if (fb) { t.style.top = Math.max(8, fb.top) + 'px'; t.style.bottom = 'auto'; t.style.left = Math.max(0, fb.left) + 'px'; t.style.right = Math.max(0, innerWidth - fb.right) + 'px'; }
    document.body.appendChild(t);
    setTimeout(function () { if (t.parentNode) { t.style.opacity = '0'; setTimeout(function () { if (t.parentNode) t.remove(); }, 350); } }, 6000);
  }

  var row = null, btn = null, info = null;
  function mount() {
    if (row && document.body.contains(row)) return;
    var area = document.querySelector('.sms-canvas-area'); if (!area) return;
    row = document.createElement('div'); row.className = 'sx-enh-float';row.hidden=true;
    row.innerHTML = '<div class="sx-enh-row"><button type="button" class="sx-enh-btn" id="sxEnhBtn" title="Upscale low-resolution images to 300 DPI">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z"/><path d="M19 15l.8 1.9 1.9.8-1.9.8L19 20.4l-.8-1.9-1.9-.8 1.9-.8z"/></svg>Enhance</button>' +
      '<span class="sx-enh-dpi" id="sxEnhDpi">Add an image to check its print quality.</span></div>';
    area.appendChild(row);
    btn = row.querySelector('#sxEnhBtn'); info = row.querySelector('#sxEnhDpi');
    btn.addEventListener('click', function () { run(); });
  }
  function refreshUi() {
    mount(); if (!btn) return;
    var imgs = images(), low = lowImages(), worst = 0;
    low.forEach(function (o) { var d = dpiOf(o); if (!worst || d < worst) worst = d; });
    // can any of them be enhanced (at most 8x from the upload)? if not, Enhance isn't offered as the next step
    var can = low.some(function (o) { var d = dpiOf(o), e = enhInfo(o), el = o.getElement(), w = el && nat(el)[0]; if (e && e.w0 && w) d = d * e.w0 / w; return d > 0 && TARGET / d <= MAX_F; });
    // the bar also holds Edit with AI (dev-studio-ai-edit.js): keep it while there's an image; Enhance + DPI only when needed
    var need = busy || !!low.length;
    row.hidden = !busy && !imgs.length; btn.hidden = !need; info.hidden = !need;
    btn.disabled = busy || !low.length;
    btn.classList.toggle('sx-go', can && !busy);
    if (busy) info.textContent = 'Enhancing…';
    else if (!imgs.length) info.textContent = 'Add an image to check its print quality.';
    else if (low.length) info.innerHTML = '<b>' + Math.round(worst) + ' DPI</b> · ' + (can ? 'below 300' : 'too small to enhance');
    else { var en = imgs.some(enhInfo); info.textContent = en ? 'Enhanced — ready to print at 300 DPI.' : 'Sharp enough to print (300 DPI).'; }
  }
  // the warning toast (section) and the proof dialog (shared utils) get an Enhance button via delegation
  document.addEventListener('click', function (ev) {
    var b = ev.target && ev.target.closest && ev.target.closest('[data-sx-enh]'); if (!b) return;
    ev.preventDefault();
    var ol = document.getElementById('sl2-proof-ol'); if (ol && ol.contains(b)) ol.remove(); // close the proof; the customer reviews again after enhancing
    var t = document.getElementById('smsDpiToast'); if (t) t.remove();
    run();
  }, true);
  new MutationObserver(function () {
    var ol = document.getElementById('sl2-proof-ol'); if (!ol) return;
    ol.querySelectorAll('.sl2-proof-warn').forEach(function (w) {
      if (w.querySelector('[data-sx-enh]') || !/resolution/i.test(w.textContent) || !lowImages().length) return;
      var b = document.createElement('button'); b.type = 'button'; b.setAttribute('data-sx-enh', ''); b.textContent = 'Enhance image'; b.style.marginLeft = '8px';
      w.appendChild(b);
    });
  }).observe(document.body, { childList: true, subtree: true });

  // while Enhance runs, other picture changes wait (its result would land on top of them)
  document.addEventListener('click', function (ev) {
    if (!busy) return;
    var b = ev.target && ev.target.closest && ev.target.closest('.sms-tool-icon[data-bg],#btnCrop'); if (!b) return;
    ev.preventDefault(); ev.stopImmediatePropagation(); toast('Wait for Enhance to finish first.', 'warn');
  }, true);
  // Undo/Redo rebuild the main image from saved data. Point the background state at the picture now on the
  // canvas, or the next background choice brings back the undone Enhance / AI edit (with no cart note).
  document.addEventListener('click', function (ev) {
    if (!(ev.target && ev.target.closest && ev.target.closest('#btnUndo,#btnRedo'))) return;
    var a = A(), m0 = a.getMainImage && a.getMainImage(), e0 = m0 && m0.getElement(), tries = 0;
    var iv = setInterval(function () {
      var s = S(), m = a.getMainImage && a.getMainImage(), el = m && m.getElement();
      if (++tries > 40) return clearInterval(iv);
      if (!el || el === e0) return;
      clearInterval(iv);
      if (!s || s.bgPending || el === s.originalImg || el === s.processedImg || !(el instanceof HTMLImageElement)) return;
      var rec = seen.get(srcKey(el)); // the base picture that was in effect when this picture was on the canvas
      if (rec && rec.o) { s.originalImg = rec.o; s.processedImg = rec.p ? el : null; return; }
      if ((s.bgMode || 'none') === 'none') { s.originalImg = el; s.processedImg = null; } else s.processedImg = el;
    }, 100);
  }, true);
  // remember, per canvas picture (by its source, which undo states keep), which upload/edit it was made from
  var seen = new Map();
  function srcKey(el) { var u = el && el.src; return u ? u.length + ':' + u.slice(-96) : ''; }
  setInterval(function () {
    var s = S(), g = A().getMainImage, m = g && g(), el = m && m.getElement(); if (!s || !el || busy || s.bgPending || !(el instanceof HTMLImageElement)) return;
    if (el !== s.originalImg && el !== s.processedImg) return; // only consistent moments
    var k = srcKey(el); if (!k) return;
    seen.delete(k); seen.set(k, { o: s.originalImg, p: el === s.processedImg });
    if (seen.size > 60) seen.delete(seen.keys().next().value);
  }, 800);

  setInterval(refreshUi, 900);
  if (!window._sxCloudEnhance && window.DTFUpscale && !isIOS()) setTimeout(function () { /* warm the GPU check early; downloads nothing */ window.DTFUpscale.available(); }, 3000);

  window._sxEnhance = { busy: function () { return busy; }, toast: toast, run: run, dpiOf: dpiOf, mainDpi: mainDpi, lowImages: lowImages, cartNote: cartNote, swap: swap }; // swap: also used by dev-studio-ai-edit.js
})();
